import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import Lenis from '@studio-freight/lenis';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

// === MUSICAL TIERS ===
const TIERS = [
  {
    id: 'sur',
    name: 'Sur',
    subtitle: 'The Root Note',
    price: '₹499',
    period: '/ month',
    accent: '#A0A0A0',
    popular: false,
    features: [
      '5 Aarohan AI Generating Prompts / mo',
      'Standard Virtual Library Access',
      'Community Forum Access',
      '720p Masterclass Streaming'
    ]
  },
  {
    id: 'sargam',
    name: 'Sargam',
    subtitle: 'The Progression',
    price: '₹1,299',
    period: '/ month',
    accent: '#C8922A',
    popular: true,
    features: [
      'Unlimited Aarohan AI Assistant',
      'Advanced Cloud Mixing Plugins',
      '10% Off All Studio Sessions',
      '1080p Masterclass Archive Library'
    ]
  },
  {
    id: 'raag',
    name: 'Raag',
    subtitle: 'The Complete Masterpiece',
    price: '₹3,499',
    period: '/ month',
    accent: '#A63B48', // Deep Crimson for Elite
    popular: false,
    features: [
      'Unlimited AI Cloud Rendering (4K Audio)',
      '1 Free Online Class Enrollment / mo',
      'Priority Dolby Atmos Studio Booking',
      'Monthly 1-on-1 Digital Mix Feedback'
    ]
  }
];

export default function SubscriptionPortal() {
  useEffect(() => {
    const lenis = new Lenis({
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smooth: true,
    });
    function raf(time) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }
    requestAnimationFrame(raf);
    window.scrollTo(0, 0);

    return () => lenis.destroy();
  }, []);

  return (
    <div style={{ background: '#0D0904', minHeight: '100vh', color: '#FAF7F0', overflowX: 'hidden' }}>
      <Navbar />

      <div style={{ paddingTop: 160, paddingBottom: 160, minHeight: '80vh', position: 'relative', zIndex: 10 }}>
        {/* Dynamic Abstract Background */}
        <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse at 50% 0%, rgba(200,146,42,0.08) 0%, transparent 60%)', zIndex: -1 }} />

        <div style={{ textAlign: 'center', marginBottom: 80, padding: '0 5%' }}>
          <span style={{ fontSize: 12, letterSpacing: '4px', color: '#C8922A', textTransform: 'uppercase', fontFamily: "'DM Sans', sans-serif", display: 'block', marginBottom: 24 }}>The Aarohan Vault</span>
          <h1 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(48px, 6vw, 84px)', fontWeight: 300, color: '#FAF7F0', margin: '0 0 24px 0', lineHeight: 1 }}>Unlock the Symphony.</h1>
          <p style={{ fontFamily: "'DM Sans', sans-serif", color: 'rgba(250,247,240,0.5)', fontSize: 16, maxWidth: 600, margin: '0 auto', lineHeight: 1.6 }}>
            Join the elite circle of musicians and producers. Get full access to Aarohan's state-of-the-art AI generation tools, cloud plugins, and exclusive masterclasses.
          </p>
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 32, justifyContent: 'center', padding: '0 5%', maxWidth: 1400, margin: '0 auto' }}>
          {TIERS.map((tier, index) => (
            <motion.div
              key={tier.id}
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: index * 0.2 }}
              style={{
                flex: '1 1 280px', maxWidth: 420,
                background: 'rgba(26,18,9,0.4)',
                border: tier.popular ? `1px solid ${tier.accent}` : '1px solid rgba(250,247,240,0.05)',
                borderRadius: 4, padding: 'clamp(32px, 8vw, 48px) clamp(24px, 6vw, 40px)',
                position: 'relative', overflow: 'hidden', cursor: 'pointer',
                boxShadow: tier.popular ? `0 0 40px ${tier.accent}11` : 'none',
              }}
              whileHover={{ y: -8, background: 'rgba(26,18,9,0.7)', borderColor: `${tier.accent}66` }}
            >
              {/* Popular Glow Effect */}
              {tier.popular && (
                <div style={{ position: 'absolute', top: 0, right: 32, background: tier.accent, color: '#0D0904', padding: '6px 16px', fontSize: 10, letterSpacing: '2px', textTransform: 'uppercase', fontFamily: "'DM Sans', sans-serif", fontWeight: 700, borderBottomLeftRadius: 4, borderBottomRightRadius: 4 }}>
                  Most Preferred
                </div>
              )}

              {/* Header */}
              <div style={{ borderBottom: '1px solid rgba(250,247,240,0.05)', paddingBottom: 32, marginBottom: 32 }}>
                <span style={{ fontSize: 11, letterSpacing: '3px', textTransform: 'uppercase', color: tier.accent, fontFamily: "'DM Sans', sans-serif", display: 'block', marginBottom: 12 }}>{tier.subtitle}</span>
                <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 48, fontWeight: 300, color: '#FAF7F0', margin: '0 0 24px 0', lineHeight: 1 }}>{tier.name}</h2>
                <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8 }}>
                  <span style={{ fontSize: 48, fontWeight: 300, color: '#FAF7F0', fontFamily: "'DM Sans', sans-serif", lineHeight: 0.9 }}>{tier.price}</span>
                  <span style={{ fontSize: 14, color: 'rgba(250,247,240,0.4)', fontFamily: "'DM Sans', sans-serif", paddingBottom: 2 }}>{tier.period}</span>
                </div>
              </div>

              {/* Features Array */}
              <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 48px 0', display: 'flex', flexDirection: 'column', gap: 16 }}>
                {tier.features.map((feature, i) => (
                  <li key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 16, fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: 'rgba(250,247,240,0.7)', lineHeight: 1.5 }}>
                    <span style={{ color: tier.accent, fontSize: 16, display: 'inline-block', transform: 'translateY(-2px)' }}>✓</span>
                    {feature}
                  </li>
                ))}
              </ul>

              {/* CTA Button */}
              <button
                onClick={() => window.location.href = `/checkout?plan=${tier.id}`}
                style={{
                  width: '100%', padding: '20px',
                  background: tier.popular ? tier.accent : 'transparent',
                  color: tier.popular ? '#0D0904' : tier.accent,
                  border: `1px solid ${tier.accent}`,
                  fontSize: 12, letterSpacing: '3px', textTransform: 'uppercase', fontFamily: "'DM Sans', sans-serif", cursor: 'pointer',
                  transition: 'all 0.3s'
                }}
                onMouseEnter={e => {
                  if (!tier.popular) {
                    e.currentTarget.style.background = `${tier.accent}22`;
                  } else {
                    e.currentTarget.style.background = 'transparent';
                    e.currentTarget.style.color = tier.accent;
                  }
                }}
                onMouseLeave={e => {
                  if (!tier.popular) {
                    e.currentTarget.style.background = 'transparent';
                  } else {
                    e.currentTarget.style.background = tier.accent;
                    e.currentTarget.style.color = '#0D0904';
                  }
                }}
              >
                Access {tier.name}
              </button>

            </motion.div>
          ))}
        </div>
      </div>

      <Footer />
    </div>
  );
}
